import type { Aggregations, AggregationsSchema, BucketsAggregationSchema } from '@/types/sammy/response';
import type { FilterOption, Filter } from '@/types/filters';
import type { z } from 'zod';

interface TranslationFunction {
    (key: string, value: string): string;
}

//Remove isPreScriptionOnly as a Checkbox but lets keep this for possible future puposes
export const CHECKBOX_FILTERS: string[] = [];

const BOOLEAN_SELECT_FIELDS = ['isPrescriptionOnly'];

export function convertAggregationsToFilters(aggs: Aggregations, t: TranslationFunction): Filter[] {
    const newFilters = [];

    for (const [key, agg] of Object.entries(aggs)) {
        if (key === 'doc_count' || key === 'unique_documents_filtered') continue;

        const isCheckbox = CHECKBOX_FILTERS.includes(key);
        const isBooleanSelect = (BOOLEAN_SELECT_FIELDS as readonly string[]).includes(key);

        const component = isCheckbox ? 'FilterCheckbox' : 'FilterSelect';
        const title = t(`pagePlp.filters.${key}`, key);
        const translateOptions = ['resultType'].includes(key);
        const props: Filter['props'] = {
            title,
            options: [],
            visible: hasDocs(agg),
            translateOptions,
            isActive: false,
            selectedOption: ''
        };

        if (isBooleanSelect) {
            const buckets =
                'buckets' in (agg as z.infer<typeof AggregationsSchema>)
                    ? ((agg as z.infer<typeof BucketsAggregationSchema>).buckets ?? [])
                    : [];

            const findCount = (want: boolean) => {
                const match = buckets.find((bucket) => {
                    const key = bucket.key;
                    return key === want || key === Number(want) || key === String(want);
                });
                if (!match) return 0;
                return 'unique_documents' in match ? (match.unique_documents?.value ?? 0) : (match.doc_count ?? 0);
            };

            const yesCount = findCount(true);
            const noCount = findCount(false);

            const options = [
                {
                    key: 'all',
                    label: t('pagePlp.filters.all', 'all')
                }
            ];

            if (yesCount > 0) {
                options.push({
                    key: 'true',
                    label: `${t('general.yes', 'Yes')} (${yesCount})`
                });
            }

            if (noCount > 0) {
                options.push({
                    key: 'false',
                    label: `${t('general.no', 'No')} (${noCount})`
                });
            }

            props.options = options;

            newFilters.push({
                component: 'FilterSelect',
                type: key,
                props
            });
            continue;
        }

        if (!isCheckbox && !(key === 'unique_documents_count')) {
            props.options = [
                {
                    key: 'all',
                    label: t('pagePlp.filters.all', 'all')
                },
                ...agg.buckets.map((bucket) => ({
                    key: bucket.key,
                    label: translateOptions
                        ? `${t(`pagePlp.filterValues.bucketKeys.${bucket.key}`, bucket.key.toString())} (${'unique_documents' in bucket ? bucket.unique_documents?.value : bucket.doc_count})`
                        : `${bucket.key} (${'unique_documents' in bucket ? bucket.unique_documents?.value : bucket.doc_count})`
                }))
            ];
        }
        newFilters.push({
            component,
            type: key,
            props
        });
    }

    return newFilters;
}

export function convertFiltersToQuery(filters: Ref<Record<string, FilterOption>>): Record<string, string | boolean> {
    const params: Record<string, string | boolean> = {};

    Object.entries(filters.value).forEach(([type, filter]) => {
        if (CHECKBOX_FILTERS.includes(type) && !filter.key) return;

        if ((BOOLEAN_SELECT_FIELDS as readonly string[]).includes(type)) {
            const key = String(filter.key);
            if (!key || key === 'all') return;

            params[`filters[${type}]`] = key === 'true' ? 'true' : 'false';
            return;
        }

        params[`filters[${type}]`] = filter.key;
    });

    return params;
}

export function restoreSelectedFilters(
    filterComponents: Filter[],
    filtersQuery: Record<string, string>
): { filters: Filter[]; selectedFilters: Record<string, FilterOption> } {
    if (!filtersQuery) return { filters: filterComponents, selectedFilters: {} };

    const selectedFilters: Record<string, FilterOption> = {};

    for (const [key, value] of Object.entries(filtersQuery)) {
        const filter = filterComponents.find((f) => f.type === key);
        if (!filter) continue;

        selectedFilters[key] = { label: filter.props.title, key: '' };

        if (filter.component === 'FilterCheckbox') {
            filter.props.isActive = value === 'true';
            selectedFilters[key].key = value === 'true';
        } else if (filter.component === 'FilterSelect') {
            filter.props.selectedOption = { key: value, label: value };
            selectedFilters[key].key = value;
        }
    }

    return { filters: filterComponents, selectedFilters };
}

function hasDocs(agg: Aggregations['filtered_aggs'][0]): boolean {
    if ('buckets' in agg) {
        if (Array.isArray(agg.buckets) && agg.buckets.length > 0)
            return agg.buckets.some((bucket) => bucket.doc_count > 0);
    }

    return false;
}
